import React, { useState, useCallback, useMemo } from 'react';
import { Button } from '../_custom';
import SaveViewModal from './SaveViewModal';
import { saveFilteredView, editFilteredView } from './FilteredViewHelper';
import { toast } from 'react-toastify';
import { Mixpanel } from '../../utils/mixpanel';
import { FilteredViewOption } from './ViewSelector';
import { isEqual } from 'lodash';
import styles from './FilteredViewsButton.module.scss';
import { saveIconPurpleFiltered } from '../../assets/hostedassets';
import { isAppliedFiltersEmpty } from '../../utils/utils';

interface ViewActionsProps {
    selectedView: FilteredViewOption | null;
    appliedFilters: any;
    userData?: any;
    organizationId?: string;
    userId?: string;
    source: string;
    existingViewNames: string[];
    countAppliedFilters: (filters: any) => number;
    filterMapMemo: Map<string, any>;
    onViewSaved: (viewId: string, viewName: string) => void;
    onRefetch: () => void;
    shouldShowSaveButton: boolean;
    currentColumns?: string[];
}

const ViewActions: React.FC<ViewActionsProps> = ({
    selectedView,
    appliedFilters,
    userData,
    organizationId,
    userId,
    source,
    existingViewNames,
    countAppliedFilters,
    filterMapMemo,
    onViewSaved,
    onRefetch,
    shouldShowSaveButton,
    currentColumns = [],
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // Computed values
    const selectedViewFilters = useMemo(
        () => (selectedView ? filterMapMemo.get(selectedView.value)?.filters : null),
        [selectedView, filterMapMemo],
    );

    const hasFiltersChanged = useMemo(
        () => selectedView && selectedViewFilters && !isEqual(appliedFilters, selectedViewFilters),
        [selectedView, selectedViewFilters, appliedFilters],
    );

    // Check if columns have changed
    const hasColumnsChanged = useMemo(() => {
        if (!selectedView) return false;
        const savedView = filterMapMemo.get(selectedView.value);
        const savedColumns = savedView?.columns || [];
        return !isEqual(currentColumns, savedColumns);
    }, [selectedView, currentColumns, filterMapMemo]);

    const isActionInProgress = isSaving || isUpdating;

    // Mixpanel tracking utility
    const trackMixpanelEvent = useCallback(
        (eventName: string, additionalProps: any = {}) => {
            Mixpanel.track(eventName, {
                currentURL: window.location.href,
                user_email: userData?.primaryEmail,
                pno_id: organizationId,
                source,
                ...additionalProps,
            });
        },
        [userData?.primaryEmail, organizationId, source],
    );

    // Direct update functionality (no modal)
    const handleDirectUpdate = useCallback(async () => {
        if (!selectedView || !organizationId || !userId) {
            toast.error('Unable to update view. Please try again.');
            return;
        }

        setIsUpdating(true);

        try {
            const filterCount = countAppliedFilters(appliedFilters);

            const result = await editFilteredView({
                viewId: selectedView.value,
                filters: isAppliedFiltersEmpty(appliedFilters) ? {} : appliedFilters,
                userId,
                columns: currentColumns,
            });

            if (result) {
                toast.success(`View "${selectedView.label}" updated successfully!`);

                trackMixpanelEvent('View Updated', {
                    filters_applied: filterCount,
                    view_name: selectedView.label,
                });

                onRefetch();
            }
        } catch (error) {
            console.error('Error updating view:', error);
            toast.error('An error occurred while updating the view.');
        } finally {
            setIsUpdating(false);
        }
    }, [selectedView, organizationId, userId, appliedFilters, countAppliedFilters, trackMixpanelEvent, onRefetch, currentColumns]);

    // Modal handlers
    const handleOpenModal = useCallback(() => {
        if (selectedView && (hasFiltersChanged || hasColumnsChanged)) {
            // For updates, call API directly without modal
            handleDirectUpdate();
        } else {
            // For new saves, open modal
            setIsModalOpen(true);
            trackMixpanelEvent('Save View Modal Opened', {
                hasSelectedView: !!selectedView,
            });
        }
    }, [selectedView, hasFiltersChanged, hasColumnsChanged, handleDirectUpdate, trackMixpanelEvent]);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    // Save new view functionality (with modal)
    const handleSaveView = useCallback(
        async (viewName: string) => {
            if (!organizationId || !userId) {
                toast.error('Unable to save view. Please try again.');
                return;
            }

            setIsSaving(true);

            try {
                const filterCount = countAppliedFilters(appliedFilters);

                const result = await saveFilteredView({
                    viewName,
                    pnoId: organizationId,
                    source,
                    filters: isAppliedFiltersEmpty(appliedFilters) ? {} : appliedFilters,
                    userId,
                    columns: currentColumns,
                });

                if (result) {
                    toast.success(`View "${viewName}" saved successfully!`);

                    trackMixpanelEvent('View Created', {
                        filters_applied: filterCount,
                        view_name: viewName,
                    });

                    onRefetch();
                    onViewSaved(result.viewId, viewName);
                    setIsModalOpen(false);
                }
            } catch (error) {
                console.error('Error saving view:', error);
                toast.error('An error occurred while saving the view.');
            } finally {
                setIsSaving(false);
            }
        },
        [organizationId, userId, appliedFilters, countAppliedFilters, source, trackMixpanelEvent, onRefetch, onViewSaved, currentColumns],
    );

    const getButtonText = useCallback(() => {
        if (isUpdating) return 'Updating...';
        if (selectedView && (hasFiltersChanged || hasColumnsChanged)) return 'Update View';
        return 'Save View';
    }, [isUpdating, selectedView, hasFiltersChanged, hasColumnsChanged]);

    if (!shouldShowSaveButton) {
        return null;
    }

    return (
        <>
            {/* Save/Update View Button with animation */}
            <div className={`${styles.SaveButtonContainer} ${shouldShowSaveButton ? styles.Show : styles.Hide}`}>
                <Button
                    text={getButtonText()}
                    variant="secondary"
                    onClick={handleOpenModal}
                    icon={saveIconPurpleFiltered}
                    disabled={isActionInProgress}
                    style={{
                        marginTop: 0,
                        marginLeft: '0.5rem',
                        borderRadius: '0.25rem',
                        height: '2.75rem',
                        opacity: isActionInProgress ? 0.7 : 1,
                        cursor: isActionInProgress ? 'not-allowed' : 'pointer',
                    }}
                />
            </div>

            <SaveViewModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveView}
                existingViewNames={existingViewNames}
                isLoading={isSaving}
                isUpdating={false} // Modal is only for new saves now
                currentViewName=""
            />
        </>
    );
};

export default ViewActions;
